import { ProposalAccessGate } from '@components/BookingProposal/ProposalAccessGate';
import { MaintenanceContent } from '@components/Common/MaintenanceScreen';
import { Error404PageWithTranslations } from '@components/Error/Error404PageWithTranslations';
import { customerRoutes } from 'noddi-util';
import { useBookingActions, useBookingProposalCustomer } from 'noddi-web';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useNewBookingPath } from '../hooks/useNewBookingPath';
import { useRegisterFlowUserGroup } from '../stores/flowUserGroupStore';
import { getNoddiRemoteConfig } from '../utils/remoteConfigSetup';
import { BookingFlowChoice } from './BookingFlow/Common/Choice';
import { Addons } from './BookingFlow/Mobile/Addons';
import { Address } from './BookingFlow/Mobile/Address';
import { Cars } from './BookingFlow/Mobile/Car';
import { InsuranceInfo } from './BookingFlow/Mobile/InsuranceInfo';
import { BookingLogin } from './BookingFlow/Mobile/Login';
import { ServiceItemsForCars } from './BookingFlow/Mobile/Services';
import { Summary } from './BookingFlow/Mobile/Summary';
import { BookingBuilder } from './BookingFlow/Mobile/Summary/BookingBuilder';
import { TimeWindow } from './BookingFlow/Mobile/TimeWindows';
import { TireHotelPickup } from './BookingFlow/Mobile/TireHotelPickup';
import { TireHotelSeason } from './BookingFlow/Mobile/TireHotelSeason';

function NewBookingIndexRedirect() {
  const { path } = useNewBookingPath();
  return <Navigate to={path} replace />;
}

/**
 * A draft built from a proposal enters at the cars step, so the index and choice
 * steps always start a new booking. A proposal marker left in the persisted draft
 * must not claim that booking's group, gate or pricing.
 */
function useProposalCustomerForNewBooking() {
  const { pathname } = useLocation();
  const storedProposalCustomer = useBookingProposalCustomer();
  const { setBookingProposalId, setBookingProposalCustomer } = useBookingActions();
  const isNewBookingStart =
    pathname.replace(/\/$/, '') === customerRoutes.newBooking.getPath() ||
    pathname.startsWith(customerRoutes.newBookingChoice.getPath());
  const isStale = isNewBookingStart && !!storedProposalCustomer;

  useEffect(() => {
    if (isStale) {
      setBookingProposalId(undefined);
      setBookingProposalCustomer(undefined);
    }
  }, [isStale, setBookingProposalId, setBookingProposalCustomer]);

  return isStale ? undefined : storedProposalCustomer;
}

export function BookingRouter() {
  const proposalCustomer = useProposalCustomerForNewBooking();
  useRegisterFlowUserGroup(proposalCustomer?.userGroupId);
  const maintenanceScreenBooking = getNoddiRemoteConfig('maintenanceScreenBooking');

  if (maintenanceScreenBooking?.shouldShow) {
    return <MaintenanceContent maintenanceScreen={maintenanceScreenBooking} />;
  }

  return (
    <ProposalAccessGate userGroupId={proposalCustomer?.userGroupId}>
      <Routes>
        <Route index element={<NewBookingIndexRedirect />} />
        <Route path={customerRoutes.newBookingChoice.getBasePath()} element={<BookingFlowChoice />} />
        <Route path={customerRoutes.newBookingAddress.getBasePath()} element={<Address />} />
        <Route path={customerRoutes.newBookingCars.getBasePath()} element={<Cars />} />
        <Route path={customerRoutes.newBookingPrimaryItems.getBasePath()} element={<ServiceItemsForCars />} />
        <Route path={customerRoutes.newBookingAddons.getBasePath()} element={<Addons />} />
        <Route path={customerRoutes.newBookingTireHotelSeason.getBasePath()} element={<TireHotelSeason />} />
        <Route path={customerRoutes.newBookingTireHotel.getBasePath()} element={<TireHotelPickup />} />
        <Route path={customerRoutes.newBookingTimeWindow.getBasePath()} element={<TimeWindow />} />
        <Route path={customerRoutes.newBookingInsuranceInfo.getBasePath()} element={<InsuranceInfo />} />
        <Route path={customerRoutes.newBookingLogin.getBasePath()} element={<BookingLogin />} />
        <Route path={customerRoutes.newBookingSummary.getBasePath()} element={<Summary />} />
        <Route path={customerRoutes.newBookingBuilding.getBasePath()} element={<BookingBuilder />} />
        <Route path='*' element={<Error404PageWithTranslations />} />
      </Routes>
    </ProposalAccessGate>
  );
}
